import React, { useState, Component } from 'react';
import Layout from '../../components/Layout';
import { UsersIcon, CalendarIcon, ArrowRightIcon, SearchIcon, FilterIcon, ChevronDownIcon, CheckIcon, XIcon, AlertTriangleIcon, EyeIcon, DownloadIcon, MessageSquareIcon, CheckCircleIcon, XCircleIcon, FlagIcon, ListIcon, GridIcon, SlidersIcon, UserIcon, FileTextIcon, ClockIcon, MoreHorizontalIcon, ChevronUpIcon, AlertCircleIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
// Sample application data
const sampleApplications = [{
  id: 'APP001',
  studentName: 'Ahmed Al Mansouri',
  studentId: 'STU12345',
  program: 'Master of Business Administration',
  status: 'Pending',
  eligibility: 'Eligible',
  submissionDate: '2023-06-15',
  lastUpdated: '2023-06-16',
  aiSummary: 'Strong candidate with high GPA and relevant work experience',
  flags: [],
  tags: ['High GPA', 'Work Experience'],
  documents: ['Transcript', 'CV', 'Recommendation Letter'],
  avatar: 'AM'
}, {
  id: 'APP002',
  studentName: 'Fatima Al Zaabi',
  studentId: 'STU12346',
  program: 'Bachelor of Computer Science',
  status: 'Under Review',
  eligibility: 'Needs Review',
  submissionDate: '2023-06-14',
  lastUpdated: '2023-06-17',
  aiSummary: 'Promising candidate with strong technical background',
  flags: ['Missing recommendation letter'],
  tags: ['Technical Skills', 'International'],
  documents: ['Transcript', 'CV'],
  avatar: 'FZ'
}, {
  id: 'APP003',
  studentName: 'Mohammed Al Hashimi',
  studentId: 'STU12347',
  program: 'PhD in Data Science',
  status: 'Accepted',
  eligibility: 'Eligible',
  submissionDate: '2023-06-10',
  lastUpdated: '2023-06-18',
  aiSummary: 'Exceptional candidate with research experience and publications',
  flags: [],
  tags: ['Research Experience', 'Publications', 'High GPA'],
  documents: ['Transcript', 'CV', 'Research Proposal', 'Publications'],
  avatar: 'MH'
}, {
  id: 'APP004',
  studentName: 'Sara Al Dhaheri',
  studentId: 'STU12348',
  program: 'Master of Public Administration',
  status: 'Rejected',
  eligibility: 'Ineligible',
  submissionDate: '2023-06-08',
  lastUpdated: '2023-06-15',
  aiSummary: 'Does not meet minimum GPA requirements',
  flags: ['GPA below minimum requirement'],
  tags: ['Work Experience'],
  documents: ['Transcript', 'CV', 'Recommendation Letter'],
  avatar: 'SD'
}, {
  id: 'APP005',
  studentName: 'Khalid Al Mazrouei',
  studentId: 'STU12349',
  program: 'Bachelor of Business Administration',
  status: 'Flagged',
  eligibility: 'Needs Review',
  submissionDate: '2023-06-12',
  lastUpdated: '2023-06-14',
  aiSummary: 'Potential candidate but transcript verification needed',
  flags: ['Transcript verification required'],
  tags: ['Leadership Experience'],
  documents: ['Transcript', 'CV', 'Personal Statement'],
  avatar: 'KM'
}, {
  id: 'APP006',
  studentName: 'Aisha Al Shamsi',
  studentId: 'STU12350',
  program: 'Master of Education',
  status: 'Pending',
  eligibility: 'Eligible',
  submissionDate: '2023-06-13',
  lastUpdated: '2023-06-13',
  aiSummary: 'Strong teaching background with excellent recommendations',
  flags: [],
  tags: ['Teaching Experience', 'High GPA'],
  documents: ['Transcript', 'CV', 'Recommendation Letters (2)'],
  avatar: 'AS'
}, {
  id: 'APP007',
  studentName: 'Omar Al Suwaidi',
  studentId: 'STU12351',
  program: 'Bachelor of Engineering',
  status: 'Under Review',
  eligibility: 'Eligible',
  submissionDate: '2023-06-11',
  lastUpdated: '2023-06-16',
  aiSummary: 'Strong technical skills with previous project experience',
  flags: [],
  tags: ['Technical Skills', 'Project Experience'],
  documents: ['Transcript', 'CV', 'Portfolio'],
  avatar: 'OS'
}, {
  id: 'APP008',
  studentName: 'Maryam Al Nuaimi',
  studentId: 'STU12352',
  program: 'Diploma in Digital Marketing',
  status: 'Pending',
  eligibility: 'Needs Review',
  submissionDate: '2023-06-14',
  lastUpdated: '2023-06-14',
  aiSummary: 'Marketing background with portfolio showing potential',
  flags: ['Missing reference contact information'],
  tags: ['Portfolio Uploaded'],
  documents: ['Transcript', 'CV', 'Portfolio'],
  avatar: 'MN'
}];
// Helper function to get status color
const getStatusColor = status => {
  switch (status) {
    case 'Accepted':
      return 'bg-green-100 text-green-800';
    case 'Rejected':
      return 'bg-red-100 text-red-800';
    case 'Pending':
      return 'bg-yellow-100 text-yellow-800';
    case 'Under Review':
      return 'bg-blue-100 text-blue-800';
    case 'Flagged':
      return 'bg-orange-100 text-orange-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};
// Helper function to get eligibility icon
const getEligibilityIcon = eligibility => {
  switch (eligibility) {
    case 'Eligible':
      return <CheckIcon className="w-5 h-5 text-green-500" data-id="element-5754" />;
    case 'Ineligible':
      return <XIcon className="w-5 h-5 text-red-500" data-id="element-5755" />;
    case 'Needs Review':
      return <AlertTriangleIcon className="w-5 h-5 text-yellow-500" data-id="element-5756" />;
    default:
      return <AlertTriangleIcon className="w-5 h-5 text-gray-500" data-id="element-5757" />;
  }
};
// Confirmation Modal Component
const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmButtonText,
  confirmButtonColor,
  icon
}) => {
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby={title} role="dialog" aria-modal="true" data-id="element-5758">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-5759">
        {/* Background overlay */}
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={onClose} data-id="element-5760"></div>
        {/* Modal positioning */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-5761">
          &#8203;
        </span>
        {/* Modal content */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-5762">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-5763">
            <div className="sm:flex sm:items-start" data-id="element-5764">
              <div className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full ${confirmButtonColor === 'bg-red-600' ? 'bg-red-100' : 'bg-green-100'} sm:mx-0 sm:h-10 sm:w-10`} data-id="element-5765">
                {icon}
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left" data-id="element-5766">
                <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title" data-id="element-5767">
                  {title}
                </h3>
                <div className="mt-2" data-id="element-5768">
                  <p className="text-sm text-gray-500" data-id="element-5769">{message}</p>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-5770">
            <button type="button" className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 ${confirmButtonColor} text-base font-medium text-white hover:${confirmButtonColor.replace('600', '700')} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:${confirmButtonColor.replace('bg-', 'ring-')} sm:ml-3 sm:w-auto sm:text-sm`} onClick={onConfirm} data-id="element-5771">
              {confirmButtonText}
            </button>
            <button type="button" className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" onClick={onClose} data-id="element-5772">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>;
};
const Applications = () => {
  const navigate = useNavigate();
  const [view, setView] = useState('table');
  const [searchQuery, setSearchQuery] = useState('');
  const [programFilter, setProgramFilter] = useState('All Programs');
  const [intakePeriodFilter, setIntakePeriodFilter] = useState('All Periods');
  const [statusFilter, setStatusFilter] = useState('All Statuses');
  const [eligibilityFilter, setEligibilityFilter] = useState('All');
  const [selectedApplications, setSelectedApplications] = useState([]);
  const [isDetailDrawerOpen, setIsDetailDrawerOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [selectAll, setSelectAll] = useState(false);
  // Confirmation modal states
  const [isAcceptModalOpen, setIsAcceptModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [actionApplicationId, setActionApplicationId] = useState(null);
  const [isBulkAction, setIsBulkAction] = useState(false);
  // Dropdown states
  const [programDropdownOpen, setProgramDropdownOpen] = useState(false);
  const [intakeDropdownOpen, setIntakeDropdownOpen] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [eligibilityDropdownOpen, setEligibilityDropdownOpen] = useState(false);
  // Sorting state
  const [sortField, setSortField] = useState('submissionDate');
  const [sortDirection, setSortDirection] = useState('desc');
  // Available filter options
  const programOptions = ['All Programs', 'Master of Business Administration', 'Bachelor of Computer Science', 'PhD in Data Science', 'Master of Public Administration', 'Bachelor of Business Administration', 'Master of Education', 'Diploma in Digital Marketing'];
  const intakePeriodOptions = ['All Periods', 'Fall 2023', 'Spring 2024', 'Summer 2024'];
  const statusOptions = ['All Statuses', 'Pending', 'Under Review', 'Accepted', 'Rejected', 'Flagged'];
  const eligibilityOptions = ['All', 'Eligible', 'Ineligible', 'Needs Review'];
  // Handle showing accept confirmation modal
  const handleShowAcceptModal = (applicationId, bulk = false) => {
    setActionApplicationId(applicationId);
    setIsBulkAction(bulk);
    setIsAcceptModalOpen(true);
  };
  // Handle showing reject confirmation modal
  const handleShowRejectModal = (applicationId, bulk = false) => {
    setActionApplicationId(applicationId);
    setIsBulkAction(bulk);
    setIsRejectModalOpen(true);
  };
  // Handle accept confirmation
  const handleAcceptConfirm = () => {
    if (isBulkAction) {
      // Handle bulk accept
      console.log('Bulk accepting applications:', selectedApplications);
      // In a real app, you would call an API to update these applications
      // Update the UI (for demo purposes)
      // In a real app, you might refetch the data instead
      toast.success(`${selectedApplications.length} applications have been accepted.`, {
        duration: 4000,
        icon: <CheckCircleIcon className="w-5 h-5" data-id="element-5773" />
      });
      setSelectedApplications([]);
      setSelectAll(false);
    } else {
      // Handle single application accept
      console.log('Accepting application:', actionApplicationId);
      // In a real app, you would call an API to update this application
      // Update the UI (for demo purposes)
      // In a real app, you might refetch the data instead
      toast.success(`Application ${actionApplicationId} has been accepted.`, {
        duration: 4000,
        icon: <CheckCircleIcon className="w-5 h-5" data-id="element-5774" />
      });
      // If the application is in the drawer, close it
      if (isDetailDrawerOpen && selectedApplication && selectedApplication.id === actionApplicationId) {
        setIsDetailDrawerOpen(false);
        setSelectedApplication(null);
      }
    }
    // Close the modal
    setIsAcceptModalOpen(false);
  };
  // Handle reject confirmation
  const handleRejectConfirm = () => {
    if (isBulkAction) {
      // Handle bulk reject
      console.log('Bulk rejecting applications:', selectedApplications);
      // In a real app, you would call an API to update these applications
      // Update the UI (for demo purposes)
      // In a real app, you might refetch the data instead
      toast.error(`${selectedApplications.length} applications have been rejected.`, {
        duration: 4000,
        icon: <XCircleIcon className="w-5 h-5" data-id="element-5775" />
      });
      setSelectedApplications([]);
      setSelectAll(false);
    } else {
      // Handle single application reject
      console.log('Rejecting application:', actionApplicationId);
      // In a real app, you would call an API to update this application
      // Update the UI (for demo purposes)
      // In a real app, you might refetch the data instead
      toast.error(`Application ${actionApplicationId} has been rejected.`, {
        duration: 4000,
        icon: <XCircleIcon className="w-5 h-5" data-id="element-5776" />
      });
      // If the application is in the drawer, close it
      if (isDetailDrawerOpen && selectedApplication && selectedApplication.id === actionApplicationId) {
        setIsDetailDrawerOpen(false);
        setSelectedApplication(null);
      }
    }
    // Close the modal
    setIsRejectModalOpen(false);
  };
  // Handle sort change
  const handleSort = field => {
    if (sortField === field) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };
  // Sort indicator component
  const SortIndicator = ({
    field
  }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 ml-1" data-id="element-5777" /> : <ChevronDownIcon className="w-4 h-4 ml-1" data-id="element-5778" />;
  };
  // Filter applications based on filters
  const filteredApplications = sampleApplications.filter(application => {
    // Search filter
    const matchesSearch = searchQuery === '' || application.studentName.toLowerCase().includes(searchQuery.toLowerCase()) || application.studentId.toLowerCase().includes(searchQuery.toLowerCase()) || application.program.toLowerCase().includes(searchQuery.toLowerCase());
    // Program filter
    const matchesProgram = programFilter === 'All Programs' || application.program === programFilter;
    // Status filter
    const matchesStatus = statusFilter === 'All Statuses' || application.status === statusFilter;
    // Eligibility filter
    const matchesEligibility = eligibilityFilter === 'All' || application.eligibility === eligibilityFilter;
    // Intake period filter - we don't have this data in our sample, so we'll assume all match
    const matchesIntake = intakePeriodFilter === 'All Periods' || true;
    return matchesSearch && matchesProgram && matchesStatus && matchesEligibility && matchesIntake;
  });
  // Sort the filtered applications
  const sortedApplications = [...filteredApplications].sort((a, b) => {
    // Handle different field types
    switch (sortField) {
      case 'studentName':
        return sortDirection === 'asc' ? a.studentName.localeCompare(b.studentName) : b.studentName.localeCompare(a.studentName);
      case 'program':
        return sortDirection === 'asc' ? a.program.localeCompare(b.program) : b.program.localeCompare(a.program);
      case 'status':
        return sortDirection === 'asc' ? a.status.localeCompare(b.status) : b.status.localeCompare(a.status);
      case 'eligibility':
        return sortDirection === 'asc' ? a.eligibility.localeCompare(b.eligibility) : b.eligibility.localeCompare(a.eligibility);
      case 'submissionDate':
        return sortDirection === 'asc' ? new Date(a.submissionDate) - new Date(b.submissionDate) : new Date(b.submissionDate) - new Date(a.submissionDate);
      default:
        return 0;
    }
  });
  // Handle checkbox selection
  const handleCheckboxChange = applicationId => {
    if (selectedApplications.includes(applicationId)) {
      setSelectedApplications(selectedApplications.filter(id => id !== applicationId));
    } else {
      setSelectedApplications([...selectedApplications, applicationId]);
    }
  };
  // Handle select all checkbox
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedApplications([]);
    } else {
      setSelectedApplications(filteredApplications.map(app => app.id));
    }
    setSelectAll(!selectAll);
  };
  // Open application detail drawer
  const openDetailDrawer = application => {
    setSelectedApplication(application);
    setIsDetailDrawerOpen(true);
  };
  // Close application detail drawer
  const closeDetailDrawer = () => {
    setIsDetailDrawerOpen(false);
    setSelectedApplication(null);
  };
  // Format date to readable format
  const formatDate = dateString => {
    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  // Get application name for confirmation messages
  const getApplicationInfo = applicationId => {
    if (isBulkAction) {
      return `${selectedApplications.length} selected applications`;
    } else {
      const app = sampleApplications.find(a => a.id === applicationId);
      return app ? `${app.studentName}'s application for ${app.program}` : 'this application';
    }
  };
  return <Layout title="Applications" persona="University Coordinator" universityName="UAE Business School" data-id="element-5779">
      {/* Top Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8" data-id="element-5780">
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-5781">
          <div className="flex items-start justify-between" data-id="element-5782">
            <div data-id="element-5783">
              <h3 className="font-medium text-gray-800 mb-2" data-id="element-5784">
                Total Applications
              </h3>
              <div className="text-3xl font-bold text-primary-500" data-id="element-5785">48</div>
              <p className="text-sm text-gray-500 mt-1" data-id="element-5786">22 new this week</p>
            </div>
            <div className="p-3 bg-primary-50 rounded-full" data-id="element-5787">
              <UsersIcon className="w-6 h-6 text-primary-500" data-id="element-5788" />
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-5789">
          <div className="flex items-start justify-between" data-id="element-5790">
            <div data-id="element-5791">
              <h3 className="font-medium text-gray-800 mb-2" data-id="element-5792">Pending Review</h3>
              <div className="text-3xl font-bold text-yellow-500" data-id="element-5793">15</div>
              <p className="text-sm text-gray-500 mt-1" data-id="element-5794">
                5 flagged for attention
              </p>
            </div>
            <div className="p-3 bg-yellow-50 rounded-full" data-id="element-5795">
              <ClockIcon className="w-6 h-6 text-yellow-500" data-id="element-5796" />
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-5797">
          <div className="flex items-start justify-between" data-id="element-5798">
            <div data-id="element-5799">
              <h3 className="font-medium text-gray-800 mb-2" data-id="element-5800">Accepted</h3>
              <div className="text-3xl font-bold text-green-500" data-id="element-5801">18</div>
              <p className="text-sm text-gray-500 mt-1" data-id="element-5802">32% acceptance rate</p>
            </div>
            <div className="p-3 bg-green-50 rounded-full" data-id="element-5803">
              <CheckCircleIcon className="w-6 h-6 text-green-500" data-id="element-5804" />
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-5805">
          <div className="flex items-start justify-between" data-id="element-5806">
            <div data-id="element-5807">
              <h3 className="font-medium text-gray-800 mb-2" data-id="element-5808">Intake Period</h3>
              <div className="text-xl font-bold text-primary-500" data-id="element-5809">
                Fall 2023
              </div>
              <p className="text-sm text-gray-500 mt-1" data-id="element-5810">Closes in 15 days</p>
            </div>
            <div className="p-3 bg-primary-50 rounded-full" data-id="element-5811">
              <CalendarIcon className="w-6 h-6 text-primary-500" data-id="element-5812" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Actions */}
      <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm mb-6" data-id="element-5813">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4" data-id="element-5814">
          <div className="relative flex-grow max-w-md" data-id="element-5815">
            <input type="text" placeholder="Search by name, ID, or keyword..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" aria-label="Search applications" data-id="element-5816" />
            <SearchIcon className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" data-id="element-5817" />
          </div>
          <div className="flex items-center space-x-2" data-id="element-5818">
            <button onClick={() => setView('table')} className={`p-2 rounded-md ${view === 'table' ? 'bg-gray-100' : 'hover:bg-gray-50'}`} title="Table View" aria-label="Switch to table view" aria-pressed={view === 'table'} data-id="element-5819">
              <ListIcon className="w-5 h-5 text-gray-600" data-id="element-5820" />
            </button>
            <button onClick={() => setView('card')} className={`p-2 rounded-md ${view === 'card' ? 'bg-gray-100' : 'hover:bg-gray-50'}`} title="Card View" aria-label="Switch to card view" aria-pressed={view === 'card'} data-id="element-5821">
              <GridIcon className="w-5 h-5 text-gray-600" data-id="element-5822" />
            </button>
          </div>
        </div>
        <div className="flex flex-wrap gap-3" data-id="element-5823">
          {/* Program Filter */}
          <div className="relative" data-id="element-5824">
            <button className="px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 flex items-center" onClick={() => {
            setProgramDropdownOpen(!programDropdownOpen);
            setIntakeDropdownOpen(false);
            setStatusDropdownOpen(false);
            setEligibilityDropdownOpen(false);
          }} data-id="element-5825">
              <FilterIcon className="w-4 h-4 mr-2 text-gray-500" data-id="element-5826" />
              Program: {programFilter}
              <ChevronDownIcon className="w-4 h-4 ml-2 text-gray-500" data-id="element-5827" />
            </button>
            {programDropdownOpen && <div className="absolute left-0 mt-1 w-64 bg-white rounded-md shadow-lg border border-gray-200 z-10" data-id="element-5828">
                <div className="py-1 max-h-60 overflow-y-auto" data-id="element-5829">
                  {programOptions.map(program => <button key={program} onClick={() => {
                setProgramFilter(program);
                setProgramDropdownOpen(false);
              }} className={`w-full text-left px-4 py-2 text-sm ${programFilter === program ? 'bg-primary-50 text-primary-500' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-5830">
                      {program}
                    </button>)}
                </div>
              </div>}
          </div>
          {/* Intake Period Filter */}
          <div className="relative" data-id="element-5831">
            <button className="px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 flex items-center" onClick={() => {
            setIntakeDropdownOpen(!intakeDropdownOpen);
            setProgramDropdownOpen(false);
            setStatusDropdownOpen(false);
            setEligibilityDropdownOpen(false);
          }} data-id="element-5832">
              <CalendarIcon className="w-4 h-4 mr-2 text-gray-500" data-id="element-5833" />
              Intake: {intakePeriodFilter}
              <ChevronDownIcon className="w-4 h-4 ml-2 text-gray-500" data-id="element-5834" />
            </button>
            {intakeDropdownOpen && <div className="absolute left-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-10" data-id="element-5835">
                <div className="py-1" data-id="element-5836">
                  {intakePeriodOptions.map(period => <button key={period} onClick={() => {
                setIntakePeriodFilter(period);
                setIntakeDropdownOpen(false);
              }} className={`w-full text-left px-4 py-2 text-sm ${intakePeriodFilter === period ? 'bg-primary-50 text-primary-500' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-5837">
                      {period}
                    </button>)}
                </div>
              </div>}
          </div>
          {/* Status Filter */}
          <div className="relative" data-id="element-5838">
            <button className="px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 flex items-center" onClick={() => {
            setStatusDropdownOpen(!statusDropdownOpen);
            setProgramDropdownOpen(false);
            setIntakeDropdownOpen(false);
            setEligibilityDropdownOpen(false);
          }} data-id="element-5839">
              <SlidersIcon className="w-4 h-4 mr-2 text-gray-500" data-id="element-5840" />
              Status: {statusFilter}
              <ChevronDownIcon className="w-4 h-4 ml-2 text-gray-500" data-id="element-5841" />
            </button>
            {statusDropdownOpen && <div className="absolute left-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-10" data-id="element-5842">
                <div className="py-1" data-id="element-5843">
                  {statusOptions.map(status => <button key={status} onClick={() => {
                setStatusFilter(status);
                setStatusDropdownOpen(false);
              }} className={`w-full text-left px-4 py-2 text-sm ${statusFilter === status ? 'bg-primary-50 text-primary-500' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-5844">
                      {status}
                    </button>)}
                </div>
              </div>}
          </div>
          {/* Eligibility Filter */}
          <div className="relative" data-id="element-5845">
            <button className="px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 flex items-center" onClick={() => {
            setEligibilityDropdownOpen(!eligibilityDropdownOpen);
            setProgramDropdownOpen(false);
            setIntakeDropdownOpen(false);
            setStatusDropdownOpen(false);
          }} data-id="element-5846">
              <CheckCircleIcon className="w-4 h-4 mr-2 text-gray-500" data-id="element-5847" />
              Eligibility: {eligibilityFilter}
              <ChevronDownIcon className="w-4 h-4 ml-2 text-gray-500" data-id="element-5848" />
            </button>
            {eligibilityDropdownOpen && <div className="absolute left-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-10" data-id="element-5849">
                <div className="py-1" data-id="element-5850">
                  {eligibilityOptions.map(eligibility => <button key={eligibility} onClick={() => {
                setEligibilityFilter(eligibility);
                setEligibilityDropdownOpen(false);
              }} className={`w-full text-left px-4 py-2 text-sm ${eligibilityFilter === eligibility ? 'bg-primary-50 text-primary-500' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-5851">
                      {eligibility}
                    </button>)}
                </div>
              </div>}
          </div>
        </div>
      </div>

      {/* Bulk Actions Bar - Only visible when applications are selected */}
      {selectedApplications.length > 0 && <div className="bg-primary-50 p-3 rounded-lg border border-primary-100 mb-6 flex items-center justify-between" data-id="element-5852">
          <div className="flex items-center" data-id="element-5853">
            <span className="text-sm font-medium text-primary-700 mr-4" data-id="element-5854">
              {selectedApplications.length} applications selected
            </span>
          </div>
          <div className="flex space-x-2" data-id="element-5855">
            <button className="px-3 py-2 bg-green-500 text-white rounded-md text-sm font-medium flex items-center hover:bg-green-600" onClick={() => handleShowAcceptModal(null, true)} data-id="element-5856">
              <CheckIcon className="w-4 h-4 mr-2" data-id="element-5857" />
              Accept
            </button>
            <button className="px-3 py-2 bg-red-500 text-white rounded-md text-sm font-medium flex items-center hover:bg-red-600" onClick={() => handleShowRejectModal(null, true)} data-id="element-5858">
              <XIcon className="w-4 h-4 mr-2" data-id="element-5859" />
              Reject
            </button>
            <button className="px-3 py-2 bg-blue-500 text-white rounded-md text-sm font-medium flex items-center hover:bg-blue-600" data-id="element-5860">
              <UserIcon className="w-4 h-4 mr-2" data-id="element-5861" />
              Assign Reviewer
            </button>
            <button className="px-3 py-2 bg-gray-500 text-white rounded-md text-sm font-medium flex items-center hover:bg-gray-600" data-id="element-5862">
              <DownloadIcon className="w-4 h-4 mr-2" data-id="element-5863" />
              Export
            </button>
          </div>
        </div>}

      {/* Table View */}
      {view === 'table' && <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-5864">
          <div className="overflow-x-auto" data-id="element-5865">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-5866">
              <thead className="bg-gray-50" data-id="element-5867">
                <tr data-id="element-5868">
                  <th scope="col" className="px-3 py-3" data-id="element-5869">
                    <div className="flex items-center" data-id="element-5870">
                      <input type="checkbox" checked={selectAll} onChange={handleSelectAll} className="h-4 w-4 rounded border-gray-300 text-primary-500 focus:ring-primary-500" aria-label="Select all applications" data-id="element-5871" />
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('studentName')} data-id="element-5872">
                    <div className="flex items-center" data-id="element-5873">
                      Student
                      <SortIndicator field="studentName" data-id="element-5874" />
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('program')} data-id="element-5875">
                    <div className="flex items-center" data-id="element-5876">
                      Program
                      <SortIndicator field="program" data-id="element-5877" />
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('status')} data-id="element-5878">
                    <div className="flex items-center" data-id="element-5879">
                      Status
                      <SortIndicator field="status" data-id="element-5880" />
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('eligibility')} data-id="element-5881">
                    <div className="flex items-center" data-id="element-5882">
                      Eligibility
                      <SortIndicator field="eligibility" data-id="element-5883" />
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('submissionDate')} data-id="element-5884">
                    <div className="flex items-center" data-id="element-5885">
                      Submission Date
                      <SortIndicator field="submissionDate" data-id="element-5886" />
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5887">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-5888">
                {sortedApplications.length > 0 ? sortedApplications.map(application => <tr key={application.id} className="hover:bg-gray-50" data-id="element-5889">
                      <td className="px-3 py-4 whitespace-nowrap" data-id="element-5890">
                        <div className="flex items-center" data-id="element-5891">
                          <input type="checkbox" checked={selectedApplications.includes(application.id)} onChange={() => handleCheckboxChange(application.id)} className="h-4 w-4 rounded border-gray-300 text-primary-500 focus:ring-primary-500" aria-label={`Select ${application.studentName}'s application`} data-id="element-5892" />
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap cursor-pointer" onClick={() => openDetailDrawer(application)} data-id="element-5893">
                        <div className="flex items-center" data-id="element-5894">
                          <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-600" data-id="element-5895">
                            {application.avatar}
                          </div>
                          <div className="ml-4" data-id="element-5896">
                            <div className="text-sm font-medium text-gray-900" data-id="element-5897">
                              {application.studentName}
                              {application.flags.length > 0 && <FlagIcon className="w-4 h-4 text-red-500 inline ml-2" data-id="element-5898" />}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-5899">
                              {application.studentId}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap cursor-pointer" onClick={() => openDetailDrawer(application)} data-id="element-5900">
                        <div className="text-sm text-gray-900" data-id="element-5901">
                          {application.program}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap cursor-pointer" onClick={() => openDetailDrawer(application)} data-id="element-5902">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(application.status)}`} data-id="element-5903">
                          {application.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap cursor-pointer" onClick={() => openDetailDrawer(application)} data-id="element-5904">
                        <div className="flex items-center" title={`Eligibility: ${application.eligibility}`} data-id="element-5905">
                          {getEligibilityIcon(application.eligibility)}
                          <span className="ml-2 text-sm text-gray-700" data-id="element-5906">
                            {application.eligibility}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 cursor-pointer" onClick={() => openDetailDrawer(application)} data-id="element-5907">
                        {formatDate(application.submissionDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-5908">
                        <div className="flex space-x-2" data-id="element-5909">
                          <button onClick={() => openDetailDrawer(application)} className="text-primary-500 hover:text-primary-700" title="View Application" data-id="element-5910">
                            
                          </button>
                          {application.status === 'Pending' && <>
                              <button className="text-green-500 hover:text-green-700" title="Accept Application" onClick={() => handleShowAcceptModal(application.id)} data-id="element-5911">
                                <CheckCircleIcon className="w-5 h-5" data-id="element-5912" />
                              </button>
                              <button className="text-red-500 hover:text-red-700" title="Reject Application" onClick={() => handleShowRejectModal(application.id)} data-id="element-5913">
                                <XCircleIcon className="w-5 h-5" data-id="element-5914" />
                              </button>
                            </>}
                          <button className="text-gray-500 hover:text-gray-700" title="More Options" data-id="element-5915">
                            <MoreHorizontalIcon className="w-5 h-5" data-id="element-5916" />
                          </button>
                        </div>
                      </td>
                    </tr>) : <tr data-id="element-5917">
                    <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-5918">
                      No applications found matching your criteria
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>
        </div>}

      {/* Card Grid View */}
      {view === 'card' && <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-5919">
          {filteredApplications.map(application => <div key={application.id} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow" data-id="element-5920">
              <div className="p-4 border-b border-gray-100 flex justify-between items-center" data-id="element-5921">
                <div className="flex items-center" data-id="element-5922">
                  <input type="checkbox" checked={selectedApplications.includes(application.id)} onChange={() => handleCheckboxChange(application.id)} className="h-4 w-4 rounded border-gray-300 text-primary-500 focus:ring-primary-500 mr-3" aria-label={`Select ${application.studentName}'s application`} data-id="element-5923" />
                  <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(application.status)}`} data-id="element-5924">
                    {application.status}
                  </span>
                </div>
                <div className="flex space-x-1" data-id="element-5925">
                  <button onClick={() => openDetailDrawer(application)} className="p-1 text-primary-500 hover:text-primary-700 rounded-full hover:bg-primary-50" title="View Application" data-id="element-5926">
                    <EyeIcon className="w-4 h-4" data-id="element-5927" />
                  </button>
                  {application.status === 'Pending' && <>
                      <button className="p-1 text-green-500 hover:text-green-700 rounded-full hover:bg-green-50" title="Accept Application" onClick={() => handleShowAcceptModal(application.id)} data-id="element-5928">
                        <CheckCircleIcon className="w-4 h-4" data-id="element-5929" />
                      </button>
                      <button className="p-1 text-red-500 hover:text-red-700 rounded-full hover:bg-red-50" title="Reject Application" onClick={() => handleShowRejectModal(application.id)} data-id="element-5930">
                        <XCircleIcon className="w-4 h-4" data-id="element-5931" />
                      </button>
                    </>}
                  <button className="p-1 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-50" title="More Options" data-id="element-5932">
                    <MoreHorizontalIcon className="w-4 h-4" data-id="element-5933" />
                  </button>
                </div>
              </div>
              <div className="p-4 cursor-pointer" onClick={() => openDetailDrawer(application)} data-id="element-5934">
                <div className="flex items-center mb-3" data-id="element-5935">
                  <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-600" data-id="element-5936">
                    {application.avatar}
                  </div>
                  <div className="ml-3" data-id="element-5937">
                    <h3 className="text-sm font-medium text-gray-900 flex items-center" data-id="element-5938">
                      {application.studentName}
                      {application.flags.length > 0 && <FlagIcon className="w-4 h-4 text-red-500 ml-2" data-id="element-5939" />}
                    </h3>
                    <div className="text-xs text-gray-500" data-id="element-5940">
                      {application.studentId}
                    </div>
                  </div>
                </div>
                <div className="mb-3" data-id="element-5941">
                  <div className="text-xs text-gray-500" data-id="element-5942">Program</div>
                  <div className="text-sm text-gray-900" data-id="element-5943">
                    {application.program}
                  </div>
                </div>
                <div className="flex justify-between mb-3" data-id="element-5944">
                  <div data-id="element-5945">
                    <div className="text-xs text-gray-500" data-id="element-5946">Eligibility</div>
                    <div className="flex items-center text-sm text-gray-900" data-id="element-5947">
                      {getEligibilityIcon(application.eligibility)}
                      <span className="ml-1" data-id="element-5948">{application.eligibility}</span>
                    </div>
                  </div>
                  <div data-id="element-5949">
                    <div className="text-xs text-gray-500" data-id="element-5950">Submitted</div>
                    <div className="text-sm text-gray-900" data-id="element-5951">
                      {formatDate(application.submissionDate)}
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 -mx-4 -mb-4 p-3 mt-4 border-t border-gray-100" data-id="element-5952">
                  <div className="text-xs text-gray-500 mb-1" data-id="element-5953">AI Summary</div>
                  <p className="text-sm text-gray-700" data-id="element-5954">
                    {application.aiSummary}
                  </p>
                  {application.tags.length > 0 && <div className="mt-2 flex flex-wrap gap-1" data-id="element-5955">
                      {application.tags.map((tag, index) => <span key={index} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800" data-id="element-5956">
                          {tag}
                        </span>)}
                    </div>}
                </div>
              </div>
            </div>)}
        </div>}

      {/* Application Detail Drawer */}
      {isDetailDrawerOpen && selectedApplication && <div className="fixed inset-0 overflow-hidden z-50" data-id="element-5957">
          <div className="absolute inset-0 overflow-hidden" data-id="element-5958">
            <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closeDetailDrawer} data-id="element-5959"></div>
            <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex" data-id="element-5960">
              <div className="relative w-screen max-w-2xl" data-id="element-5961">
                <div className="h-full flex flex-col bg-white shadow-xl overflow-y-scroll" data-id="element-5962">
                  {/* Header */}
                  <div className="px-6 py-4 bg-primary-500 sm:px-6" data-id="element-5963">
                    <div className="flex items-start justify-between" data-id="element-5964">
                      <h2 className="text-lg font-medium text-white" data-id="element-5965">
                        Application Details
                      </h2>
                      <button onClick={closeDetailDrawer} className="bg-primary-500 rounded-md text-white hover:text-gray-200 focus:outline-none" data-id="element-5966">
                        <XIcon className="h-6 w-6" data-id="element-5967" />
                      </button>
                    </div>
                  </div>
                  {/* Content */}
                  <div className="flex-1 overflow-y-auto p-6" data-id="element-5968">
                    {/* Student Info */}
                    <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6" data-id="element-5969">
                      <div className="flex items-center mb-4" data-id="element-5970">
                        <div className="h-16 w-16 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-xl" data-id="element-5971">
                          {selectedApplication.avatar}
                        </div>
                        <div className="ml-4" data-id="element-5972">
                          <h3 className="text-xl font-medium text-gray-900" data-id="element-5973">
                            {selectedApplication.studentName}
                          </h3>
                          <p className="text-gray-500" data-id="element-5974">
                            {selectedApplication.studentId}
                          </p>
                          <div className="mt-1 flex items-center" data-id="element-5975">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(selectedApplication.status)}`} data-id="element-5976">
                              {selectedApplication.status}
                            </span>
                            <div className="ml-3 flex items-center text-sm text-gray-700" data-id="element-5977">
                              {getEligibilityIcon(selectedApplication.eligibility)}
                              <span className="ml-1" data-id="element-5978">
                                {selectedApplication.eligibility}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Application Summary */}
                      <div className="mb-6" data-id="element-5979">
                        <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-5980">
                          Application Summary
                        </h4>
                        <div className="bg-gray-50 p-4 rounded-md" data-id="element-5981">
                          <div className="grid grid-cols-2 gap-4" data-id="element-5982">
                            <div data-id="element-5983">
                              <div className="text-xs text-gray-500" data-id="element-5984">
                                Program
                              </div>
                              <div className="text-sm font-medium" data-id="element-5985">
                                {selectedApplication.program}
                              </div>
                            </div>
                            <div data-id="element-5986">
                              <div className="text-xs text-gray-500" data-id="element-5987">
                                Intake Period
                              </div>
                              <div className="text-sm font-medium" data-id="element-5988">
                                Fall 2023
                              </div>
                            </div>
                            <div data-id="element-5989">
                              <div className="text-xs text-gray-500" data-id="element-5990">
                                Submitted On
                              </div>
                              <div className="text-sm font-medium" data-id="element-5991">
                                {formatDate(selectedApplication.submissionDate)}
                              </div>
                            </div>
                            <div data-id="element-5992">
                              <div className="text-xs text-gray-500" data-id="element-5993">
                                Last Updated
                              </div>
                              <div className="text-sm font-medium" data-id="element-5994">
                                {formatDate(selectedApplication.lastUpdated)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* AI Insights */}
                      <div className="mb-6" data-id="element-5995">
                        <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-5996">
                          AI Insights
                        </h4>
                        <div className="bg-primary-50 p-4 rounded-md border border-primary-100" data-id="element-5997">
                          <p className="text-sm text-gray-700 mb-3" data-id="element-5998">
                            {selectedApplication.aiSummary}
                          </p>
                          {selectedApplication.flags.length > 0 && <div className="mb-3" data-id="element-5999">
                              <div className="text-xs font-medium text-red-600 mb-1" data-id="element-6000">
                                Flags:
                              </div>
                              <ul className="list-disc list-inside text-sm text-red-600" data-id="element-6001">
                                {selectedApplication.flags.map((flag, index) => <li key={index} data-id="element-6002">{flag}</li>)}
                              </ul>
                            </div>}
                          {selectedApplication.tags.length > 0 && <div className="flex flex-wrap gap-1" data-id="element-6003">
                              {selectedApplication.tags.map((tag, index) => <span key={index} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800" data-id="element-6004">
                                  {tag}
                                </span>)}
                            </div>}
                        </div>
                      </div>
                      {/* Documents */}
                      <div className="mb-6" data-id="element-6005">
                        <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-6006">
                          Uploaded Documents
                        </h4>
                        <div className="space-y-2" data-id="element-6007">
                          {selectedApplication.documents.map((doc, index) => <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-md" data-id="element-6008">
                              <div className="flex items-center" data-id="element-6009">
                                <FileTextIcon className="w-5 h-5 text-gray-400 mr-3" data-id="element-6010" />
                                <span className="text-sm text-gray-700" data-id="element-6011">
                                  {doc}
                                </span>
                              </div>
                              <button className="text-primary-500 hover:text-primary-700 text-sm" data-id="element-6012">
                                View
                              </button>
                            </div>)}
                        </div>
                      </div>
                      {/* Activity Log */}
                      <div data-id="element-6013">
                        <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-6014">
                          Activity Log
                        </h4>
                        <div className="space-y-3" data-id="element-6015">
                          <div className="flex items-start" data-id="element-6016">
                            <div className="flex-shrink-0" data-id="element-6017">
                              <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center" data-id="element-6018">
                                <ClockIcon className="h-4 w-4 text-blue-600" data-id="element-6019" />
                              </div>
                            </div>
                            <div className="ml-3" data-id="element-6020">
                              <p className="text-sm text-gray-700" data-id="element-6021">
                                Application status changed to{' '}
                                <span className="font-medium" data-id="element-6022">
                                  {selectedApplication.status}
                                </span>
                              </p>
                              <p className="text-xs text-gray-500" data-id="element-6023">
                                {formatDate(selectedApplication.lastUpdated)} by
                                System
                              </p>
                            </div>
                          </div>
                          <div className="flex items-start" data-id="element-6024">
                            <div className="flex-shrink-0" data-id="element-6025">
                              <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center" data-id="element-6026">
                                <FileTextIcon className="h-4 w-4 text-green-600" data-id="element-6027" />
                              </div>
                            </div>
                            <div className="ml-3" data-id="element-6028">
                              <p className="text-sm text-gray-700" data-id="element-6029">
                                Application submitted
                              </p>
                              <p className="text-xs text-gray-500" data-id="element-6030">
                                {formatDate(selectedApplication.submissionDate)}{' '}
                                by {selectedApplication.studentName}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Footer Actions */}
                  <div className="flex-shrink-0 px-4 py-4 flex justify-between border-t border-gray-200" data-id="element-6031">
                    <div data-id="element-6032">
                      {selectedApplication.status === 'Pending' && <button className="px-4 py-2 bg-red-500 text-white rounded-md text-sm font-medium hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500" onClick={() => handleShowRejectModal(selectedApplication.id)} data-id="element-6033">
                          Reject
                        </button>}
                    </div>
                    <div className="flex space-x-3" data-id="element-6034">
                      <button className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500" data-id="element-6035">
                        Message Student
                      </button>
                      {selectedApplication.status === 'Pending' && <button className="px-4 py-2 bg-green-500 text-white rounded-md text-sm font-medium hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500" onClick={() => handleShowAcceptModal(selectedApplication.id)} data-id="element-6036">
                          Accept
                        </button>}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>}

      {/* Accept Confirmation Modal */}
      <ConfirmationModal isOpen={isAcceptModalOpen} onClose={() => setIsAcceptModalOpen(false)} onConfirm={handleAcceptConfirm} title="Accept Application" message={`Are you sure you want to accept ${getApplicationInfo(actionApplicationId)}? This will notify the student and move the application to the accepted status.`} confirmButtonText="Accept" confirmButtonColor="bg-green-600" icon={<CheckCircleIcon className="h-6 w-6 text-green-600" aria-hidden="true" data-id="element-6038" />} data-id="element-6037" />

      {/* Reject Confirmation Modal */}
      <ConfirmationModal isOpen={isRejectModalOpen} onClose={() => setIsRejectModalOpen(false)} onConfirm={handleRejectConfirm} title="Reject Application" message={`Are you sure you want to reject ${getApplicationInfo(actionApplicationId)}? This action cannot be undone and will notify the student.`} confirmButtonText="Reject" confirmButtonColor="bg-red-600" icon={<AlertCircleIcon className="h-6 w-6 text-red-600" aria-hidden="true" data-id="element-6040" />} data-id="element-6039" />
    </Layout>;
};
export default Applications;